const WORDS_ONLY = (text) => (text || "").match(/\S+/g) || [];

const mergeAdjacent = (parts) => {
  const merged = [];
  for (const part of parts) {
    const last = merged[merged.length - 1];
    if (last && last.type === part.type) {
      last.value += part.value;
    } else {
      merged.push({ ...part });
    }
  }
  return merged;
};

const wordLcsLength = (aWords, bWords) => {
  const m = aWords.length;
  const n = bWords.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      if (aWords[i - 1] === bWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  return dp[m][n];
};

/** 0–1 score: how much word overlap two strings share. */
export const wordOverlapSimilarity = (oldText, newText) => {
  const oldWords = WORDS_ONLY(oldText).map((w) => w.toLowerCase());
  const newWords = WORDS_ONLY(newText).map((w) => w.toLowerCase());

  if (oldWords.length === 0 && newWords.length === 0) return 1;
  if (oldWords.length === 0 || newWords.length === 0) return 0;

  const lcsLen = wordLcsLength(oldWords, newWords);
  return lcsLen / Math.max(oldWords.length, newWords.length);
};

const SIMILARITY_THRESHOLD = 0.45;

const lcsDiff = (oldItems, newItems) => {
  const m = oldItems.length;
  const n = newItems.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      if (oldItems[i - 1] === newItems[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const result = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldItems[i - 1] === newItems[j - 1]) {
      result.unshift({ type: "equal", value: oldItems[i - 1] });
      i -= 1;
      j -= 1;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: "insert", value: newItems[j - 1] });
      j -= 1;
    } else {
      result.unshift({ type: "delete", value: oldItems[i - 1] });
      i -= 1;
    }
  }

  return result;
};

const joinWords = (words) => words.join(" ");

/** Word-level diff (words only — whitespace is not matched separately). */
const diffWordsInternal = (oldText, newText) => {
  const oldWords = WORDS_ONLY(oldText);
  const newWords = WORDS_ONLY(newText);

  if (oldWords.length === 0 && newWords.length === 0) return [];
  if (oldWords.length === 0) {
    return [{ type: "insert", value: joinWords(newWords) }];
  }
  if (newWords.length === 0) {
    return [{ type: "delete", value: joinWords(oldWords) }];
  }

  const raw = lcsDiff(oldWords, newWords);
  const spaced = raw.map((part, index) => ({
    type: part.type,
    value: part.value + (index < raw.length - 1 ? " " : ""),
  }));

  return mergeAdjacent(spaced);
};

const collapseNoisyWordDiff = (parts) => {
  if (parts.length === 0) return parts;

  const result = [];
  let index = 0;

  while (index < parts.length) {
    if (parts[index].type === "equal") {
      result.push(parts[index]);
      index += 1;
      continue;
    }

    let end = index;
    while (end < parts.length && parts[end].type !== "equal") {
      end += 1;
    }

    const hunk = parts.slice(index, end);
    const deleted = hunk
      .filter((p) => p.type === "delete")
      .map((p) => p.value)
      .join("");
    const inserted = hunk
      .filter((p) => p.type === "insert")
      .map((p) => p.value)
      .join("");

    const alternations = hunk.reduce((count, part, i) => {
      if (i === 0) return count;
      return count + (part.type !== hunk[i - 1].type ? 1 : 0);
    }, 0);

    const shouldCollapse =
      alternations >= 2 &&
      wordOverlapSimilarity(deleted, inserted) < SIMILARITY_THRESHOLD;

    if (shouldCollapse) {
      if (deleted.trim()) result.push({ type: "delete", value: deleted });
      if (inserted.trim()) result.push({ type: "insert", value: inserted });
    } else {
      result.push(...hunk);
    }

    index = end;
  }

  return mergeAdjacent(result);
};

const diffWords = (oldText, newText) => {
  const parts = diffWordsInternal(oldText, newText);
  return collapseNoisyWordDiff(parts);
};

const joinLines = (lines) => lines.join("\n");

const diffLineHunk = (deletedLines, insertedLines) => {
  const oldChunk = joinLines(deletedLines);
  const newChunk = joinLines(insertedLines);

  if (deletedLines.length === 0) {
    return [{ type: "insert", value: newChunk }];
  }
  if (insertedLines.length === 0) {
    return [{ type: "delete", value: oldChunk }];
  }

  if (wordOverlapSimilarity(oldChunk, newChunk) >= SIMILARITY_THRESHOLD) {
    return diffWords(oldChunk, newChunk);
  }

  const parts = [];
  if (oldChunk) parts.push({ type: "delete", value: oldChunk });
  if (newChunk) parts.push({ type: "insert", value: newChunk });
  return parts;
};

/**
 * Diff AI note (old) vs final note (new).
 * Uses line-level blocks for substantially different sections; word-level only
 * when lines share enough overlap.
 */
export const diffNotes = (oldText, newText) => {
  const oldLines = (oldText ?? "").split("\n");
  const newLines = (newText ?? "").split("\n");
  const lineParts = lcsDiff(oldLines, newLines);

  const result = [];
  let index = 0;

  while (index < lineParts.length) {
    const part = lineParts[index];

    if (part.type === "equal") {
      const value =
        part.value +
        (index < lineParts.length - 1 || oldLines.length > 1 ? "\n" : "");
      result.push({ type: "equal", value });
      index += 1;
      continue;
    }

    const deletedLines = [];
    const insertedLines = [];

    while (index < lineParts.length && lineParts[index].type === "delete") {
      deletedLines.push(lineParts[index].value);
      index += 1;
    }
    while (index < lineParts.length && lineParts[index].type === "insert") {
      insertedLines.push(lineParts[index].value);
      index += 1;
    }

    const hunkParts = diffLineHunk(deletedLines, insertedLines);
    const hunkEndsWithNewline =
      index < lineParts.length ||
      deletedLines.length + insertedLines.length <
        Math.max(oldLines.length, newLines.length);

    for (let h = 0; h < hunkParts.length; h += 1) {
      const hunkPart = hunkParts[h];
      const isLastInHunk = h === hunkParts.length - 1;
      result.push({
        type: hunkPart.type,
        value:
          hunkPart.value +
          (isLastInHunk && hunkEndsWithNewline ? "\n" : ""),
      });
    }
  }

  return mergeAdjacent(result);
};
